import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  useGetAccountTypeQuery,
  useCreateAccountTypeMutation,
  useUpdateAccountTypeMutation,
} from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { showSuccess, showError } from '../../../utils/toast';

const AdminAccountTypeForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const { data, isLoading } = useGetAccountTypeQuery(id!, { skip: !isEdit });
  const [createAccountType, { isLoading: isCreating }] = useCreateAccountTypeMutation();
  const [updateAccountType, { isLoading: isUpdating }] = useUpdateAccountTypeMutation();

  const [formData, setFormData] = useState({ title: '', title_ar: '', status: true });

  useEffect(() => {
    if (isEdit && data?.data) {
      const t = data.data;
      setFormData({
        title: t.title || '',
        title_ar: t.title_ar || '',
        status: t.status === 1 || t.status === true,
      });
    }
  }, [isEdit, data]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || formData.title.trim().length < 2) {
      showError('Title is required (min 2 characters).');
      return;
    }
    if (!formData.title_ar.trim() || formData.title_ar.trim().length < 2) {
      showError('Arabic title is required (min 2 characters).');
      return;
    }

    try {
      if (isEdit) {
        const result = await updateAccountType({ id: Number(id), ...formData }).unwrap();
        showSuccess(result.message || 'Account type updated!');
      } else {
        const result = await createAccountType(formData).unwrap();
        showSuccess(result.message || 'Account type created!');
      }
      navigate('/admin/account-types');
    } catch (err: any) {
      showError(err?.data?.message || 'Something went wrong.');
    }
  };

  if (isEdit && isLoading) return <PageLoader />;

  const isSaving = isCreating || isUpdating;

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">

        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? 'Edit Account Type' : 'Add Account Type'}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <Link to="/admin" className="text-muted text-hover-primary">Home</Link>
                </li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">
                  <Link to="/admin/account-types" className="text-muted text-hover-primary">Account Types</Link>
                </li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">{isEdit ? 'Edit' : 'Add'}</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold text-dark">
                  {isEdit ? 'Edit Account Type' : 'New Account Type'}
                </h3>
              </div>
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit}>
                  <div className="row g-5">

                    <div className="col-md-6">
                      <label className="form-label required fw-semibold">Title (English)</label>
                      <input
                        type="text"
                        name="title"
                        className="form-control form-control-solid"
                        placeholder="e.g. Individual"
                        value={formData.title}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label required fw-semibold">Title (Arabic)</label>
                      <input
                        type="text"
                        name="title_ar"
                        className="form-control form-control-solid"
                        placeholder="e.g. فردي"
                        value={formData.title_ar}
                        onChange={handleChange}
                        dir="rtl"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Status</label>
                      <div className="form-check form-switch mt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="status"
                          checked={formData.status}
                          onChange={handleChange}
                          style={{ cursor: 'pointer' }}
                        />
                        <label className="form-check-label ms-2 text-muted">
                          {formData.status ? 'Active' : 'Inactive'}
                        </label>
                      </div>
                    </div>

                  </div>

                  <div className="d-flex justify-content-end gap-3 mt-8">
                    <button type="button" className="btn btn-light" onClick={() => navigate('/admin/account-types')}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-base" disabled={isSaving}>
                      {isSaving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminAccountTypeForm;
